import React from "react";
import type { GetServerSidePropsContext } from "next";
import nr from "newrelic";
import type { FortressOptions } from "@foundation/fortress/lib/types";
import { parseGeoInfo } from "@foundation/sparkle/lib/adDials/parseGeoInfo";
import { PageWrapper, PageWrapperProps } from "src/~components/PageWrapper";
import { watchlistServerSideProps } from "~data/watchlist";
import type { PageMetadata } from "~types/types";
import WatchlistPageLayout from "~components/WatchlistPageLayout";
import { CLIENT_FEATURE_FLAG } from "~constants/Portfolio";
import { getAdConfig } from "~api/getAdConfig";

export type WatchlistProps = PageWrapperProps & {
  userId: string;
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { res } = context;
  const clientFeatureFlag =
    context.query.bzcff || context.query.bundle || "N/A";

  const pageProps: PageWrapperProps =
    await watchlistServerSideProps.load<PageWrapperProps>({
      pageType: "watchlist-bazaar",
    });

  if (pageProps.partial) {
    res.statusCode = 304;
    res.setHeader("Cache-Control", "no-cache");

    nr.addCustomAttributes({
      partialDataLoad: true,
    });

    return { props: { pageProps: { partial: true } as WatchlistProps } };
  }

  const adConfig = getAdConfig({
    ...parseGeoInfo(context.req.cookies?.geo_info),
    adCode: "markets",
  });

  return {
    props: {
      ...pageProps,
      pageType: "watchlist-bazaar",
      countryCode: context.req.cookies?.country_code || "US",
      pageData: {
        enableWatchDisV1WithLots:
          clientFeatureFlag === CLIENT_FEATURE_FLAG.WATCH_DIS_V1_WITH_LOTS,
        enableWatchDisV2:
          clientFeatureFlag === CLIENT_FEATURE_FLAG.WATCH_DIS_V2,
        enableTariffBundles:
          clientFeatureFlag === CLIENT_FEATURE_FLAG.TARIFF_BUNDLES,
        enableWatchlistImportExport:
          clientFeatureFlag === CLIENT_FEATURE_FLAG.WATCH_IMPORT_EXPORT,
      },
      adConfig,
    },
  };
};

const analytics = {
  adcode: "markets/watchlist",
  brand: "markets",
  contentType: "marketdata-watchlist",
  pageType: "watchlist",
  template: "bazaar-watchlist-html",
};

const fortressOptions: FortressOptions = {
  articleInfo: {
    contentId: "watchlist",
    metadata: {
      isMetered: true,
    },
    resourceType: "watchlist",
    resourceSubType: "watchlist",
    type: "WATCHLIST",
  },
  paywallData: {
    type: "additive" as const,
  },
};

const metadata: PageMetadata = {
  constructedTitle: "Watchlist - Bloomberg - Bloomberg Markets",
  description:
    "Bloomberg.com offers a unique tool that helps you get timely and accurate information on stocks, funds, currencies, rates, indexes and more - all in one place to help you make smarter decisions",
  displayName: "Watchlist - Bloomberg",
  pageType: "watchlist",
  title: "Watchlist - Bloomberg",
  type: "company",
  url: "https://www.bloomberg.com/markets/watchlist",
};

export default function Watchlist(props: WatchlistProps) {
  const pageDataProps = {
    ...props,
    fortressOptions,
    analytics,
    metadata,
    // Paywall is initially hidden until user does a certain action such as attempting to create a watchlist as a non-subscribed user
    disablePaywall: true,
  };

  return (
    <>
      <PageWrapper {...pageDataProps}>
        <WatchlistPageLayout />
      </PageWrapper>
    </>
  );
}
